import React, { useState, useRef } from 'react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Document, Page, pdfjs } from "react-pdf";
import { ArrowLeft, ArrowRight, Trash2, Plus } from 'lucide-react';

// --- THE FIX: Use the CDN for the worker to avoid Webpack bundling errors ---
// pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
// --- Enhanced Styles Object ---
const styles = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.75)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modalContent: {
        backgroundColor: '#ffffff',
        padding: '24px',
        borderRadius: '12px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    },
    header: {
        marginTop: 0,
        marginBottom: '4px',
        textAlign: 'center',
        fontSize: '1.25rem',
        fontWeight: '600',
        color: '#111827',
    },
    subHeader: {
        textAlign: 'center',
        color: '#6b7280',
        fontSize: '0.9rem',
        marginBottom: '16px',
    },
    signatureCount: {
        textAlign: 'center',
        color: '#059669',
        fontSize: '0.9rem',
        fontWeight: '500',
        marginBottom: '8px',
    },
    paginationContainer: {
        margin: '10px 0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px',
    },
    pageIndicator: {
        color: '#374151',
        fontSize: '1rem',
        fontWeight: '500',
        minWidth: '100px',
        textAlign: 'center',
    },
    pdfContainer: {
        flex: 1,
        overflowY: 'auto',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        position: 'relative',
        background: '#f9fafb',
    },
    overlay: {
        position: 'relative',
        cursor: 'crosshair',
        display: 'flex',
        justifyContent: 'center',
    },
    buttonContainer: {
        marginTop: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
    },
    leftButtons: {
        display: 'flex',
        gap: '12px',
    },
    rightButtons: {
        display: 'flex',
        gap: '12px',
    },
    // --- Button Styles ---
    baseButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '10px 20px',
        border: 'none',
        borderRadius: '8px',
        fontSize: '1rem',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, transform 0.1s ease',
    },
    navButton: {
        padding: '8px 12px',
        backgroundColor: '#e5e7eb',
        color: '#374151',
    },
    addButton: {
        backgroundColor: '#059669',
        color: 'white',
    },
    clearButton: {
        backgroundColor: '#dc2626',
        color: 'white',
    },
    confirmButton: {
        backgroundColor: '#2563eb',
        color: 'white',
    },
    cancelButton: {
        backgroundColor: '#ffffff',
        color: '#374151',
        border: '1px solid #d1d5db',
    },
    buttonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    // --- Signature Box Styles ---
    signatureBox: {
        position: 'absolute',
        border: '2px solid #2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.2)',
        zIndex: 10,
        borderRadius: '4px',
        cursor: 'pointer',
    },
    currentSignatureBox: {
        position: 'absolute',
        border: '2px dashed #2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.2)',
        zIndex: 10,
        borderRadius: '4px',
    },
    signatureLabel: {
        position: 'absolute',
        top: '-25px',
        left: '0px',
        backgroundColor: '#2563eb',
        color: 'white',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: '500',
        whiteSpace: 'nowrap',
    },
    deleteButton: {
        position: 'absolute',
        top: '-25px',
        right: '0px',
        backgroundColor: '#dc2626',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        width: '20px',
        height: '20px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
    },
    // --- Confirmation Modal Styles ---
    confirmModalContent: {
        backgroundColor: '#ffffff',
        padding: '24px',
        borderRadius: '12px',
        textAlign: 'center',
        maxWidth: '400px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    },
    confirmModalText: {
        marginBottom: '20px',
        fontSize: '1.1rem',
        color: '#374151',
    },
    confirmModalButtons: {
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
    }
};

const SignaturePad = ({ pdfFile, onSign, onCancel }) => {
    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageDetails, setPageDetails] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentBox, setCurrentBox] = useState(null);
    const [signatures, setSignatures] = useState([]);
    const [isConfirmingClear, setIsConfirmingClear] = useState(false);
    const overlayRef = useRef(null);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const onPageLoadSuccess = (page) => {
        setPageDetails({
            width: page.width,
            height: page.height,
            originalWidth: page.originalWidth,
            originalHeight: page.originalHeight,
        });
    };

    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prevPage => prevPage - 1);
            setCurrentBox(null);
        }
    };

    const goToNextPage = () => {
        if (currentPage < numPages) {
            setCurrentPage(prevPage => prevPage + 1);
            setCurrentBox(null);
        }
    };

    const convertCoordsToPdfPoints = (pixelBox, pageNum) => {
        if (!pageDetails || !overlayRef.current) return null;
        const { originalWidth, originalHeight } = pageDetails;
        const { width: renderedWidth, height: renderedHeight } = overlayRef.current.getBoundingClientRect();
        
        const x1 = Math.min(pixelBox.start.x, pixelBox.end.x);
        const y1 = Math.min(pixelBox.start.y, pixelBox.end.y);
        const x2 = Math.max(pixelBox.start.x, pixelBox.end.x);
        const y2 = Math.max(pixelBox.start.y, pixelBox.end.y);

        const pdfX1 = (x1 / renderedWidth) * originalWidth;
        const pdfX2 = (x2 / renderedWidth) * originalWidth;
        const pdfY1 = ((renderedHeight - y2) / renderedHeight) * originalHeight;
        const pdfY2 = ((renderedHeight - y1) / renderedHeight) * originalHeight;

        return {
            coords: `${pdfX1.toFixed(2)},${pdfY1.toFixed(2)},${pdfX2.toFixed(2)},${pdfY2.toFixed(2)}`,
            page: pageNum,
            pixelBox: pixelBox
        };
    };

    const handleMouseDown = (e) => {
        e.preventDefault();
        const { offsetX, offsetY } = e.nativeEvent;
        setIsDrawing(true);
        setCurrentBox({ start: { x: offsetX, y: offsetY }, end: { x: offsetX, y: offsetY } });
    };

    const handleMouseMove = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        const { offsetX, offsetY } = e.nativeEvent;
        setCurrentBox(prev => ({ ...prev, end: { x: offsetX, y: offsetY } }));
    };

    const handleMouseUp = () => {
        if (!isDrawing) return;
        setIsDrawing(false);
    };

    const addSignature = () => {
        if (!currentBox || !currentBox.end) {
            console.error("Attempted to add signature without a drawn box.");
            return;
        }

        const width = Math.abs(currentBox.start.x - currentBox.end.x);
        const height = Math.abs(currentBox.start.y - currentBox.end.y);
        if (width < 50 || height < 20) {
            console.warn("Signature box is too small.");
            return;
        }

        const signatureData = convertCoordsToPdfPoints(currentBox, currentPage);
        if (signatureData) {
            setSignatures(prev => [
                ...prev,
                {
                    id: Date.now(),
                    ...signatureData,
                    label: `Signature ${prev.length + 1}`
                }
            ]);
            setCurrentBox(null);
        } else {
            console.error("Could not calculate signature coordinates.");
        }
    };

    const deleteSignature = (signatureId) => {
        setSignatures(prev => prev.filter(sig => sig.id !== signatureId));
    };

    const clearAllSignatures = () => {
        if (signatures.length > 0) {
            setIsConfirmingClear(true);
        }
    };
    
    const handleConfirmClear = () => {
        setSignatures([]);
        setIsConfirmingClear(false);
    };

    const handleConfirm = () => {
        if (signatures.length === 0) {
            console.warn("Confirmation attempted with no signatures.");
            return;
        }
        onSign(signatures);
    };
    
    const getCurrentBoxStyle = () => {
        if (!currentBox) return {};
        const x = Math.min(currentBox.start.x, currentBox.end.x);
        const y = Math.min(currentBox.start.y, currentBox.end.y);
        const width = Math.abs(currentBox.start.x - currentBox.end.x);
        const height = Math.abs(currentBox.start.y - currentBox.end.y);
        return {
            ...styles.currentSignatureBox,
            left: `${x}px`,
            top: `${y}px`,
            width: `${width}px`,
            height: `${height}px`,
        };
    };

    const getSignatureBoxStyle = (signature) => {
        const x = Math.min(signature.pixelBox.start.x, signature.pixelBox.end.x);
        const y = Math.min(signature.pixelBox.start.y, signature.pixelBox.end.y);
        const width = Math.abs(signature.pixelBox.start.x - signature.pixelBox.end.x);
        const height = Math.abs(signature.pixelBox.start.y - signature.pixelBox.end.y);
        return {
            ...styles.signatureBox,
            left: `${x}px`,
            top: `${y}px`,
            width: `${width}px`,
            height: `${height}px`,
        };
    };

    const currentPageSignatures = signatures.filter(sig => sig.page === currentPage);

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
                <h3 style={styles.header}>Draw Signature Areas</h3>
                <p style={styles.subHeader}>
                    Click and drag to create signature boxes. You can add multiple signatures on any page.
                </p>
                
                {signatures.length > 0 && (
                    <div style={styles.signatureCount}>
                        {signatures.length} signature area{signatures.length !== 1 ? 's' : ''} created
                    </div>
                )}
                
                <div style={styles.paginationContainer}>
                    <button 
                        onClick={goToPrevPage} 
                        disabled={currentPage <= 1}
                        style={{...styles.baseButton, ...styles.navButton, ...(currentPage <= 1 && styles.buttonDisabled)}}
                    >
                        <ArrowLeft size={18} />
                        Previous
                    </button>
                    <span style={styles.pageIndicator}>
                        Page {currentPage} of {numPages}
                        {currentPageSignatures.length > 0 && (
                            <div style={{fontSize: '0.8rem', color: '#059669', marginTop: '2px'}}>
                                {currentPageSignatures.length} signature{currentPageSignatures.length !== 1 ? 's' : ''} on this page
                            </div>
                        )}
                    </span>
                    <button 
                        onClick={goToNextPage} 
                        disabled={currentPage >= numPages}
                        style={{...styles.baseButton, ...styles.navButton, ...(currentPage >= numPages && styles.buttonDisabled)}}
                    >
                        Next
                        <ArrowRight size={18} />
                    </button>
                </div>

                <div style={styles.pdfContainer}>
                    <Document file={pdfFile} onLoadSuccess={onDocumentLoadSuccess}>
                        <div
                            ref={overlayRef}
                            style={styles.overlay}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            {currentBox && <div style={getCurrentBoxStyle()}></div>}
                            
                            {currentPageSignatures.map((signature) => (
                                <div key={signature.id}>
                                    <div style={getSignatureBoxStyle(signature)}>
                                        <div style={styles.signatureLabel}>
                                            {signature.label}
                                        </div>
                                        <button
                                            style={styles.deleteButton}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteSignature(signature.id);
                                            }}
                                            title="Delete signature area"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            
                            <Page
                                pageNumber={currentPage}
                                onLoadSuccess={onPageLoadSuccess}
                                renderTextLayer={false}
                            />
                        </div>
                    </Document>
                </div>
                
                <div style={styles.buttonContainer}>
                    <div style={styles.leftButtons}>
                        <button 
                            onClick={addSignature}
                            disabled={!currentBox || !currentBox.end}
                            style={{
                                ...styles.baseButton, 
                                ...styles.addButton, 
                                ...(!currentBox || !currentBox.end ? styles.buttonDisabled : {})
                            }}
                        >
                            <Plus size={18} />
                            Add Signature
                        </button>
                        <button 
                            onClick={clearAllSignatures}
                            disabled={signatures.length === 0}
                            style={{
                                ...styles.baseButton, 
                                ...styles.clearButton,
                                ...(signatures.length === 0 ? styles.buttonDisabled : {})
                            }}
                        >
                            <Trash2 size={18} />
                            Clear All
                        </button>
                    </div>
                    
                    <div style={styles.rightButtons}>
                        <button onClick={onCancel} style={{...styles.baseButton, ...styles.cancelButton}}>
                            Cancel
                        </button>
                        <button 
                            onClick={handleConfirm} 
                            disabled={signatures.length === 0}
                            style={{
                                ...styles.baseButton, 
                                ...styles.confirmButton,
                                ...(signatures.length === 0 ? styles.buttonDisabled : {})
                            }}
                        >
                            Confirm All Signatures ({signatures.length})
                        </button>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal for Clearing Signatures */}
            {isConfirmingClear && (
                <div style={styles.modalOverlay}>
                    <div style={styles.confirmModalContent}>
                        <p style={styles.confirmModalText}>
                            Are you sure you want to clear all signature areas?
                        </p>
                        <div style={styles.confirmModalButtons}>
                            <button
                                onClick={() => setIsConfirmingClear(false)}
                                style={{ ...styles.baseButton, ...styles.cancelButton }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmClear}
                                style={{ ...styles.baseButton, ...styles.clearButton }}
                            >
                                Yes, Clear All
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignaturePad;